import { Injectable } from '@angular/core';
import { FetchPolicy } from '@apollo/client/core';
import { FilterParser } from '@core/filter-parser';
import { PageFilter } from '@core/model/pagination/filter';
import { PageResult } from '@core/model/pagination/page-result';
import { LocalStorageDataService } from '@core/services/local-storage.service';
import { ObjectUtils, strictMap } from '@core/utils/object-utils';
import {
  TicketsFilterInput,
  GetPaginatedTktTicketsGQL,
  AddTicketsGQL,
  GetTktTicketGQL,
  GetTicketsDataGQL,
  GetTicketsNotCompletedGQL,
  GetTicketNoGQL,
  UpdateTicketsGQL,
  UpdateTicketPriorityGQL,
  UpdateTicketEstimatedTimeGQL,
  UpdateTicketPlanDateGQL,
  UpdateTicketProblemGQL,
  UpdateTicketPriorityWithPriorityGQL,
  ScheduleTicketsGQL,
  ArchiveTicketsGQL,
  RestoreTicketsGQL,
  TicketsSortInput,
  SortEnumType,
  UpdateTicketBookmarkGQL,
  UpdateTicketDueDateGQL,
  UpdateTicketOrderGQL,
  GetPaginatedTktTicketsGridGQL,
  GetTktTicketGridGQL,
} from '@generated/queries';
import {
  Ticket,
  TicketFilters,
  UpdateTicketBookMark,
  UpdateTicketDueDate,
  UpdateTicketOrder,
  UpdateTicketPlanDateRange,
  UpdateTicketPriority,
} from '../models/tickets';
import { plainToInstance } from 'class-transformer';
import { Observable, Subscription, firstValueFrom, map } from 'rxjs';
import {
  CreateTicket,
  UpdateTicket,
  UpdateTicketProblem,
} from '../models/tickets';
import { TicketLabor } from '../models/ticket-labor';

@Injectable({
  providedIn: 'root',
})
export class TicketsService {
  timeZone: string;
  selectedTicketId: string;
  private currentRequestSub: Subscription | null = null;
  ticketLaborList: TicketLabor[];
  ticket$: Observable<PageResult<Ticket>>;
  tickets$: Observable<PageResult<Ticket>>;
  pageFilter: PageFilter = {
    pageIndex: 0,
    activeIndex: 1,
    take: 10,
    search: '',
    state: '',
  };
  vmFilters: TicketsFilterInput;
  selectedTicketNo: string;
  selectedScheduleId: string;
  //eslint-disable-next-line
  ticketFilters: any;

  constructor(
    private localStorageService: LocalStorageDataService,
    private paginatedTickets: GetPaginatedTktTicketsGQL,
    private ticketAdd: AddTicketsGQL,
    private getTicket: GetTktTicketGQL,
    private getTicketGrid: GetTktTicketGridGQL,
    private getTickestData: GetTicketsDataGQL,
    private getTickestNotCompleted: GetTicketsNotCompletedGQL,
    private getTicketNo: GetTicketNoGQL,
    private ticketUpdate: UpdateTicketsGQL,
    private ticketUpdatePriority: UpdateTicketPriorityGQL,
    private ticketUpdateOrder: UpdateTicketOrderGQL,
    private ticketUpdateEstimatedTime: UpdateTicketEstimatedTimeGQL,
    private ticketPlanDateUpdate: UpdateTicketPlanDateGQL,
    private ticketBookMark: UpdateTicketBookmarkGQL,
    private ticketUpdateProblem: UpdateTicketProblemGQL,
    private ticketDueDateUpdate: UpdateTicketDueDateGQL,
    private ticketUpdatePriorityandStatus: UpdateTicketPriorityWithPriorityGQL,
    private scheduleTickets: ScheduleTicketsGQL,
    private ticketArchive: ArchiveTicketsGQL,
    private ticketRestore: RestoreTicketsGQL,
    private paginatedTicketsGrid: GetPaginatedTktTicketsGridGQL,
  ) {}

    //eslint-disable-next-line
    async getPaginatedTickets(
    filter: PageFilter,
    siteManagerId: string,
    reportedById: string,
    assignedUserId: string,
    policy: FetchPolicy = null,
    siteSearch: string = null,
    statusSearch: string[] = null,
    prioritySearch: string = null,
    problemSearch: string = null,
    facilitySearch: string = null,
    areaSearch: string = null,
    orderBy?: TicketsSortInput,
    fromDate?: string,
    toDate?: string,
    displayType?: string,
    ticketScheduleId?: string,
    isBookMark?: boolean,
    fromPlanDate?: string,
    toPlanDate?: string
  ): Promise<PageResult<Ticket>> {
    // 🔴 1. Cancel previous ongoing request if there is one
    if (this.currentRequestSub) {
      this.currentRequestSub.unsubscribe();
      this.currentRequestSub = null;
    }

    // 🟢 2. Build base filter (search/ticketNo filter)
    this.vmFilters = {
      or: [
        {
          ticketNo: {
            contains: filter.search ? filter.search.trim() : '',
          },
        },
      ],
    };

    // 🟢 3. Add site manager filters
    if (siteManagerId) {
      this.vmFilters = {
        or: [
          { reportedById: { eq: siteManagerId } },
          {
            ticketLabor: {
              some: { workerId: { eq: siteManagerId } },
            },
          },
        ],
      };
    }

    // 🟢 4. Add all other filters dynamically
    const additionalFilters = [];
    if (siteManagerId) {
      additionalFilters.push({ ticketNo: { contains: filter.search } });
    }
    if (siteSearch) {
      additionalFilters.push({
        ticketLocationMap: { some: { locationId: { eq: siteSearch } } },
      });
    }
    if (facilitySearch) {
      additionalFilters.push({ facilityId: { eq: facilitySearch } });
    }
    if (areaSearch) {
      additionalFilters.push({
        ticketAreaMap: { some: { areaId: { eq: areaSearch } } },
      });
    }
    if (problemSearch) {
      additionalFilters.push({ problemId: { eq: problemSearch } });
    }
    if (isBookMark) {
      additionalFilters.push({ ticketBookmark: { eq: true } });
    }
    if (prioritySearch) {
      additionalFilters.push({ priorityId: { eq: prioritySearch } });
    }
    if (statusSearch?.length > 0) {
      additionalFilters.push({ currentChangeStatusId: { in: statusSearch } });
    }
    if (ticketScheduleId) {
      additionalFilters.push({ ticketScheduleId: { eq: ticketScheduleId } });
    }
    if (displayType) {
      additionalFilters.push({ ticketNo: { contains: displayType } });
    }
    if (fromDate && toDate) {
      const from = new Date(fromDate);
      const to = new Date(toDate);
      additionalFilters.push({
        dueDate: {
          gte: from.toISOString().split('T')[0],
          lte: to.toISOString().split('T')[0] + 'T23:59:59',
        },
      });
    }
    if (fromPlanDate && toPlanDate) {
      const from = new Date(fromPlanDate);
      from.setHours(0, 0, 0, 0);
      const to = new Date(toPlanDate);
      to.setHours(23, 59, 59, 999);
      additionalFilters.push({
        or: [
          { planStartDate: { gte: from, lte: to } },
          { planEndDate: { gte: from, lte: to } },
          { and: [{ planStartDate: { lte: from } }, { planEndDate: { gte: to } }] },
        ],
      });
    }

    if (additionalFilters.length > 0) {
      this.vmFilters = {
        and: [this.vmFilters, ...additionalFilters],
      };
    }

    // 🟢 5. Parse filters and pagination
    const filters = FilterParser.parseFilter({ state: filter.state });
    if (!ObjectUtils.isObjEmpty(filters)) {
      this.vmFilters = { and: [filters] };
    }
    const paginationFilter = FilterParser.parsePageFilter(filter);

    const order: TicketsSortInput[] = orderBy
      ? [orderBy]
      : [{ entryDate: SortEnumType.Desc }];

    // 🟢 6. Return Promise that resolves when latest subscription emits data
    return new Promise<PageResult<Ticket>>((resolve, reject) => {
      this.currentRequestSub = this.paginatedTickets
        .fetch(
          {
            filter: this.vmFilters,
            skip: paginationFilter.skip,
            take: paginationFilter.take,
            order,
          },
          {
            fetchPolicy: policy
              ? policy
              : this.localStorageService.getFetchPolicy('tickets'),
          }
        )
        //eslint-disable-next-line
        .pipe(map((x) => new PageResult<Ticket>(x.data.supportTicket.tickets as any)))
        .subscribe({
          next: (result) => resolve(result),
          error: (err) => reject(err),
          complete: () => (this.currentRequestSub = null),
        });
    });
  }

  getPaginatedTicketsGrid(
  filter: PageFilter,
  siteManagerId: string,
  reportedById: string,
  assignedUserId: string,
  policy: FetchPolicy = null,
  siteSearch: string = null,
  statusSearch: string[] = null,
  prioritySearch: string = null,
  problemSearch: string = null,
  facilitySearch: string = null,
  areaSearch: string = null,
  orderBy?: TicketsSortInput,
  fromDate?: string,
  toDate?: string,
  displayType?: string,
  ticketScheduleId?: string,
  isBookMark?: boolean,
  fromPlanDate?: string,
  toPlanDate?: string,
  fromEntryDate?: string,
  toEntryDate?: string,
  schedle:boolean=false
): Promise<PageResult<Ticket>> {
  this.setVmFilters(filter);

  const paginationFilter = FilterParser.parsePageFilter(filter);
  const order = this.buildOrder(orderBy);

  const { utcDateFromDate, utcDateToDate } = this.parseDateRange(fromDate, toDate);
  const { fromPlan, toPlan } = this.parsePlanDateRange(fromPlanDate, toPlanDate);
  const { fromEntry, toEntry } = this.parseEntryDateRange(fromEntryDate, toEntryDate);

  const ticketFilters = this.buildTicketFilters(
    filter,
    siteManagerId,
    reportedById,
    assignedUserId,
    siteSearch,
    facilitySearch,
    areaSearch,
    prioritySearch,
    problemSearch,
    statusSearch,
    displayType,
    utcDateFromDate,
    utcDateToDate,
    fromPlan,
    toPlan,
    fromEntry,
    toEntry,
    isBookMark,
    ticketScheduleId
  );

  return firstValueFrom(
    this.paginatedTicketsGrid
      .fetch(
        {
          filter: ticketFilters,
          skip: paginationFilter.skip,
          take: paginationFilter.take,
          order,
          schedle:schedle
        },
        {
          fetchPolicy: policy ?? this.localStorageService.getFetchPolicy('tickets'),
        }
      )
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .pipe(map((x) => new PageResult<Ticket>(x.data.supportTicket.ticketsGrid as any)))
  );
}

// ---------------- Helper Methods ---------------- //
private setVmFilters(filter: PageFilter): void {
  const filters = FilterParser.parseFilter({ state: filter.state });
  if (!ObjectUtils.isObjEmpty(filters)) {
    this.vmFilters = { and: [filters] };
  }
}

private buildOrder(orderBy?: TicketsSortInput): TicketsSortInput[] {
  return orderBy ? [orderBy] : [{ entryDate: SortEnumType.Desc }];
}

private parseDateRange(fromDate?: string, toDate?: string): { utcDateFromDate: string | null; utcDateToDate: string | null } {
  if (!(fromDate && toDate)) return { utcDateFromDate: null, utcDateToDate: null };

  const from = new Date(fromDate).toISOString().split('T')[0];
  const to = new Date(toDate).toISOString().split('T')[0];
  return { utcDateFromDate: from, utcDateToDate: to };
}

private parsePlanDateRange(fromPlanDate?: string, toPlanDate?: string): { fromPlan: Date | null; toPlan: Date | null } {
  if (!(fromPlanDate && toPlanDate)) return { fromPlan: null, toPlan: null };

  const from = new Date(fromPlanDate);
  from.setHours(0, 0, 0, 0);

  const to = new Date(toPlanDate);
  to.setHours(23, 59, 59, 999);

  return { fromPlan: from, toPlan: to };
}
private parseEntryDateRange(fromEntryDate?: string, toEntryDate?: string): { fromEntry: Date | null; toEntry: Date | null } {
  if (!(fromEntryDate && toEntryDate)) return { fromEntry: null, toEntry: null };

  const from = new Date(fromEntryDate);
  from.setHours(0, 0, 0, 0);

  const to = new Date(toEntryDate);
  to.setHours(23, 59, 59, 999);

  return { fromEntry: from, toEntry: to };
}

private buildTicketFilters(
  filter: PageFilter,
  siteManagerId: string,
  reportedById: string,
  assignedUserId: string,
  siteSearch: string,
  facilitySearch: string,
  areaSearch: string,
  prioritySearch: string,
  problemSearch: string,
  statusSearch: string[],
  displayType: string,
  utcDateFromDate: string | null,
  utcDateToDate: string | null,
  fromPlan: Date | null,
  toPlan: Date | null,
  fromEntry: Date | null,
  toEntry: Date | null,
  isBookMark: boolean,
  ticketScheduleId: string
): TicketFilters {
  return {
    facilityId: this.nullIfEmpty(facilitySearch),
    siteId: this.nullIfEmpty(siteSearch),
    areaId: this.nullIfEmpty(areaSearch),
    priorityId: this.nullIfEmpty(prioritySearch),
    problemId: this.nullIfEmpty(problemSearch),
    managerId: this.nullIfEmpty(siteManagerId),
    reportedById: this.nullIfEmpty(reportedById),
    workerId: this.nullIfEmpty(assignedUserId),
    statusIds: statusSearch?.length ? statusSearch : null,
    ticketType: this.nullIfEmpty(displayType),
    ticketNo: this.nullIfEmpty(filter.search),
    ticketScheduleId: this.nullIfEmpty(ticketScheduleId),
    isBookMark: isBookMark,
    fromEntryDate: fromEntry,
    toEntryDate: toEntry,
    fromDueDate: utcDateFromDate ? new Date(utcDateFromDate) : null,
    toDueDate: utcDateToDate ? new Date(utcDateToDate) : null,
    planStartDate: fromPlan,
    planEndDate: toPlan,
  };
}

private nullIfEmpty(value?: string): string | null {
  return value && value.trim() !== '' ? value : null;
}


  getPaginatedTicketsAll(
    policy: FetchPolicy = null,
    ): Promise<PageResult<Ticket>> {

    return firstValueFrom(
      this.paginatedTickets
        .fetch(
          {

          },
          {
            fetchPolicy: policy
              ? policy
              : this.localStorageService.getFetchPolicy('tickets'),
          }
        )
        .pipe(
          map(  //eslint-disable-next-line
            (x) => new PageResult<Ticket>(x.data.supportTicket.tickets as any)
          )
        )
    );
  }

  getTickets(id: string, fetchPolicy?: FetchPolicy): Promise<Ticket> {
    return firstValueFrom(
      this.getTicket
        .fetch(
          {
            id,
          },
          {
            fetchPolicy:
              fetchPolicy ?? this.localStorageService.getFetchPolicy('tickets'),
          }
        )
        .pipe(
          map((x) =>
            plainToInstance(Ticket, x.data.supportTicket.tickets.items[0])
          )
        )
    );
  }

  getTicketsGrid(id: string, fetchPolicy?: FetchPolicy): Promise<Ticket> {
    return firstValueFrom(
      this.getTicketGrid
        .fetch(
          {
            id,
          },
          {
            fetchPolicy:
              fetchPolicy ?? this.localStorageService.getFetchPolicy('tickets'),
          }
        )
        .pipe(
          map((x) =>
            plainToInstance(Ticket, x.data.supportTicket.tickets.items[0])
          )
        )
    );
  }

  getTicketsByDateRange(
    fromDate: Date,
    toDate: Date,
    ticketType: string,
    fetchPolicy?: FetchPolicy
  ): Promise<Ticket[]> {
    return firstValueFrom(
      this.getTickestData
        .fetch(
          {
            fromDate: fromDate,
            toDate: toDate,
            ticketType: ticketType,
          },
          {
            fetchPolicy:
              fetchPolicy ?? this.localStorageService.getFetchPolicy('tickets'),
          }
        )
        .pipe(
          map((x) => plainToInstance(Ticket, x.data.supportTicket.ticketsData))
        )
    );
  }

  getTicketsNotCompleted(
    ticketType: string,
    fetchPolicy?: FetchPolicy
  ): Promise<Ticket[]> {
    return firstValueFrom(
      this.getTickestNotCompleted
        .fetch(
          {
            ticketType: ticketType,
          },
          {
            fetchPolicy:
              fetchPolicy ?? this.localStorageService.getFetchPolicy('tickets'),
          }
        )
        .pipe(
          map((x) =>
            plainToInstance(Ticket, x.data.supportTicket.ticketsNotCompleted)
          )
        )
    );
  }

  getTicketsNo(): Promise<string> {
    return firstValueFrom(
      this.getTicketNo
        .fetch(
          {},
          {
            fetchPolicy: 'network-only',
          }
        )
        .pipe(map((x) => x.data.supportTicket.ticketNo))
    );
  }

  addTicket(model: Ticket): Promise<string> {
    let mappedModel;
    try {
      mappedModel = strictMap(CreateTicket, model);
    } catch (mappingError) {
      return Promise.reject(mappingError);
    }
    return firstValueFrom(
      this.ticketAdd
        .mutate({ model: mappedModel })
        .pipe(map((x) => x.data.supportTicket.tickets.add))
    );
  }

  updateTicket(model: Ticket): Promise<string> {
    return firstValueFrom(
      this.ticketUpdate
        .mutate({ model: strictMap(UpdateTicket, model) })
        .pipe(map((x) => x.data.supportTicket.tickets.update))
    );
  }

  updateTicketPriority(model: UpdateTicketPriority): Promise<string> {
    return firstValueFrom(
      this.ticketUpdatePriority
        .mutate({ model: strictMap(UpdateTicketPriority, model) })
        .pipe(map((x) => x.data.supportTicket.tickets.ticketPriority))
    );
  }

  updateTicketOrder(model: UpdateTicketOrder): Promise<string> {
      return firstValueFrom(
        this.ticketUpdateOrder
          .mutate({ model }) // ✅ send plain object
          .pipe(map((x) => x.data.supportTicket.tickets.updateOrder))
      );
    }


  updateTicketEstimatedTime(model: Ticket): Promise<string> {
    return firstValueFrom(
      this.ticketUpdateEstimatedTime
        .mutate({ model: strictMap(UpdateTicket, model) })
        .pipe(map((x) => x.data.supportTicket.tickets.ticketEstimatedTime))
    );
  }

  updateTicketPlanDate(model:UpdateTicketPlanDateRange): Promise<string> {
    return firstValueFrom(
      this.ticketPlanDateUpdate
        .mutate({ model: strictMap(UpdateTicketPlanDateRange, model) })
        .pipe(map((x) => x.data.supportTicket.tickets.updatePlanDates))
    );
  }

  updateTicketBookmark(model: UpdateTicketBookMark): Promise<string> {
    return firstValueFrom(
      this.ticketBookMark
        .mutate({ model: strictMap(UpdateTicketBookMark, model) })
        .pipe(map((x) => x.data.supportTicket.tickets.updateBookmark))
    );
  }

  updateTicketProblem(model: Ticket): Promise<string> {
    return firstValueFrom(
      this.ticketUpdateProblem
        .mutate({ model: strictMap(UpdateTicketProblem, model) })
        .pipe(map((x) => x.data.supportTicket.tickets.updateTicketProblem))
    );
  }

  updateTicketDueDate(model: UpdateTicketDueDate): Promise<string> {
    return firstValueFrom(this.ticketDueDateUpdate
      .mutate({ model: strictMap(UpdateTicketDueDate, model) })
      .pipe(map((x) => x.data.supportTicket.tickets.updateDueDate)));
  }

  updateTicketPriorityWithStatus(model: Ticket): Promise<string> {
    return firstValueFrom(
      this.ticketUpdatePriorityandStatus
        .mutate({ model: strictMap(UpdateTicket, model) })
        .pipe(map((x) => x.data.supportTicket.tickets.ticketPriorityWithStatus))
    );
  }

  scheduleTicket(model: Ticket): Promise<string> {
    return firstValueFrom(
      this.scheduleTickets
        .mutate({ model: strictMap(UpdateTicket, model) })
        .pipe(map((x) => x.data.supportTicket.tickets.scheduleTicket))
    );
  }

  archiveTicket(id: string): Promise<string> {
    return firstValueFrom(
      this.ticketArchive
        .mutate({ ticketId: id })
        .pipe(map((x) => x.data.supportTicket.tickets.archive))
    );
  }

  restoreTicket(id: string): Promise<string> {
    return firstValueFrom(
      this.ticketRestore
        .mutate({ ticketId: id })
        .pipe(map((x) => x.data.supportTicket.tickets.restore))
    );
  }

  fetchTickets(event: boolean): void {
    if (event) {
      this.pageFilter.search = '';
      this.pageFilter.skip = 0;
    }
    this.vmFilters = {
      or: [
        {
          ticketNo: {
            contains: this.pageFilter.search.trim(),
          },
        },
      ],
    };
    const ticket = this.paginatedTickets
      .fetch({
        filter: this.vmFilters,
        skip: this.pageFilter.skip,
        take: this.pageFilter.take,
      })
      .pipe(  //eslint-disable-next-line
        map((x) => new PageResult<Ticket>(x.data.supportTicket.tickets as any))
      );
    this.tickets$ = ticket;
  }

  fetchNextTickets(event: number): void {
    this.pageFilter.skip = event;
    this.fetchTickets(false);
  }

  //eslint-disable-next-line
  searchInTickets(event: any): void {
    this.pageFilter.search = event.term;
    this.pageFilter.skip = null;
    this.fetchTickets(false);
  }
}
